// apps/api/src/qxcircle/qxcircle-profile-view.ts
// Description: QXCircle author profile builder — keeps service under line cap.
// Owner: Masa Bayu
// Date: 2026-08-24

import { NotFoundException } from "./_nestjs-common";
import { isQxCirclePostVisible } from "./qxcircle-block";
import { qxCircleFollowHandles } from "./qxcircle-follow-graph";
import { findProfileSeed } from "./qxcircle-profile-seeds";
import { normalizeQxCircleAuthorHandle } from "./qxcircle-service-helpers";
import type { QxCircleAuthorProfile, QxCircleFeedPost } from "./qxcircle.types";

export function buildQxCircleAuthorProfile(
  handleRaw: string,
  posts: QxCircleFeedPost[],
  hiddenPosts: Set<string>,
  blockedHandles: Set<string>,
  localFollows: Set<string>,
  localMutes: Set<string>,
  present: (post: QxCircleFeedPost) => QxCircleFeedPost,
): QxCircleAuthorProfile {
  const authorHandle = normalizeQxCircleAuthorHandle(handleRaw);
  const seed = findProfileSeed(authorHandle);
  const authorPost = posts.find((p) => p.authorHandle === authorHandle);
  if (!seed && !authorPost) {
    throw new NotFoundException(`Author not found: ${authorHandle}`);
  }
  const visiblePosts = posts
    .filter(
      (p) =>
        p.authorHandle === authorHandle &&
        isQxCirclePostVisible(p, hiddenPosts, blockedHandles),
    )
    .map((p) => present(p))
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  return {
    authorHandle,
    authorName: seed?.authorName ?? authorPost?.authorName ?? authorHandle,
    bio: seed?.bio ?? "QXCircle author",
    followerCount: qxCircleFollowHandles(
      authorHandle,
      "followers",
      localFollows,
    ).filter((handle) => !blockedHandles.has(handle)).length,
    followingCount: qxCircleFollowHandles(
      authorHandle,
      "following",
      localFollows,
    ).filter((handle) => !blockedHandles.has(handle)).length,
    followedByLocal: localFollows.has(authorHandle),
    blockedByLocal: blockedHandles.has(authorHandle),
    mutedByLocal: localMutes.has(authorHandle),
    postCount: visiblePosts.length,
    posts: visiblePosts,
    world: "qxcircle",
    slice: "profile",
  };
}
